// Layer 3 (Transformations): Daily meeting (Module 10) — pure projections
// over `meeting/seed.ts`. paul-tarp.A20 (#422), the `a20-meeting` stream.
// No React, no engine mutations — every function here takes data in and
// returns data out.

import { bottleneckRead } from '@/versions/v1/modules/ingestion/Scene.transform';
import type { AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import { previousShiftWindow } from '@/versions/v1/shared/clock.derive';
import { crewLabelById, previousCrewForShift } from '@/versions/v1/shared/bunker-hill-org-seed';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { seriesAt, valueAt } from '@/versions/v1/shared/series.derive';
import type { SessionState } from '@/versions/v1/schema/scenario';
import {
  ALL_MEETING_INSTANCES,
  AREA_ORDER_BOTTLENECK_FIRST,
  CCC_CASES,
  CONTROL_VERIFICATION_CHECKS,
  HANDOVER_SAFETY_ITEMS,
  HANDOVER_TASKS,
  MEETING_CURSOR,
  PARETO_30D_COUNTS,
  PREVIOUS_MEETING_CURSOR,
  PROCESS_AREAS,
  THREE_W_ENTRIES,
  type CCCCase,
  type ControlVerificationCheck,
  type HandoverSafetyItem,
  type HandoverTask,
  type ProcessArea,
  type ThreeWEntry,
} from './seed';

export function meetingCopy() {
  return { title: 'Daily meeting' };
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `YYYY-MM-DD` → `17 Jun` — the one date grammar every tab uses, matching
 * the TopBar's own `08 Jun` form (fixer round, MF15). */
export function formatCommitDate(date: string | null | undefined): string {
  if (!date) return '—';
  const match = date.match(/^\d{4}-(\d{2})-(\d{2})/);
  if (!match) return date;
  const month = MONTH_SHORT[Number(match[1]) - 1] ?? match[1];
  return `${match[2]} ${month}`;
}

/** `YYYY-MM-DD` → `June 2026` — used once, for the CCC review period header. */
export function formatMonthYear(date: string): string {
  const match = date.match(/^(\d{4})-(\d{2})/);
  if (!match) return date;
  const month = MONTH_SHORT[Number(match[2]) - 1] ?? match[2];
  return `${month} ${match[1]}`;
}

// --- Step 1 · Previous 3Ws ------------------------------------------------

export type PreviousThreeWsBucket = {
  done: readonly ThreeWEntry[];
  slipped: readonly ThreeWEntry[];
  overdue: readonly ThreeWEntry[];
};

/** Rows due since the last meeting (`asOf`, default the previous meeting's
 * date) — done (awaiting the room's ack to close), slipped (re-committed at
 * least once, still open), overdue (never re-committed, still open). Rows
 * raised today are not "previous" — Step 2/3 own those. */
export function previousThreeWs(asOf: string = PREVIOUS_MEETING_CURSOR): PreviousThreeWsBucket {
  const due = THREE_W_ENTRIES.filter((row) => row.originalCommit <= asOf);
  const done = due.filter((row) => row.doneAwaitingAck);
  const pending = due.filter((row) => !row.doneAwaitingAck && row.currentCommit !== null);
  return {
    done,
    slipped: pending.filter((row) => row.currentCommit! > row.originalCommit),
    overdue: pending.filter((row) => row.currentCommit! === row.originalCommit),
  };
}

// --- Last 24 hours against plan --------------------------------------------

export type Variation = {
  id: string;
  label: string;
  detail: string;
  threeWIds: readonly string[];
};
function fmt1(n: number): string {
  return n.toFixed(1);
}

/** Reads against plan that produced a 3W row this window — cited, not
 * restated as an unlinked id bag (fixer round, MF4). */
function variationsThisWindow(state: SessionState): readonly Variation[] {
  const znRec = readingByIdLookup('rd-kpi-zn-rec');
  const znRecActual = valueAt('rd-kpi-zn-rec', state) ?? znRec?.value ?? 0;
  const znRecDesign = znRec?.design ?? znRecActual;
  const znDelta = znRecActual - znRecDesign;

  const bmFeed = readingByIdLookup('rd-kpv-bm-feed');
  const bmFeedActual = valueAt('rd-kpv-bm-feed', state) ?? bmFeed?.value ?? 0;
  const bmFeedDesign = bmFeed?.design ?? bmFeedActual;

  const paste = readingByIdLookup('rd-unit-ps01-paste');
  const pasteActual = valueAt('rd-unit-ps01-paste', state) ?? paste?.value ?? 0;
  const pasteTarget = paste?.target ?? pasteActual;

  return [
    {
      id: 'zn-rec-design',
      label: 'Zn recovery',
      detail: `${fmt1(znRecActual)}% vs design ${fmt1(znRecDesign)}% (${znDelta >= 0 ? '+' : ''}${fmt1(znDelta)} pt)`,
      threeWIds: ['3W-112', '3W-123'],
    },
    {
      id: 'bm01-feed',
      label: 'BM01 fresh feed',
      detail: `${fmt1(bmFeedActual)} stph vs design ${fmt1(bmFeedDesign)} stph`,
      threeWIds: ['3W-105'],
    },
    {
      id: 'paste-backfill',
      label: 'Paste backfill',
      detail: `${pasteActual} t vs ${pasteTarget} t/d`,
      threeWIds: ['3W-116'],
    },
  ];
}

export type Last24hVsPlan = {
  bottleneck: string;
  variations: readonly Variation[];
};

/** The bottleneck read imported from the plant model (Module 1) — never
 * restated here — and the variations this window produced, each linked to
 * the 3W row it raised. Both render on the `24 h performance` tab, beside
 * the windowed table the plan misses are read off. */
export function last24hVsPlan(state: SessionState, advisory?: AdvisorySessionState): Last24hVsPlan {
  return {
    bottleneck: bottleneckRead(state, advisory),
    variations: variationsThisWindow(state),
  };
}

// --- Shift handover · last 12 hours against plan ---------------------------

function seriesValueAt(seriesId: string, state: SessionState, stamp: string): number | undefined {
  const view = seriesAt(seriesId, state);
  return view.points.find((p) => p.at === stamp)?.value ?? undefined;
}

export type Last12hVsPlan = {
  windowLabel: string;
  rows: readonly (PlanKpiRow & { deltaPct: number; tone: 'ok' | 'warn' | 'bad' })[];
};

/** One row of a plan-vs-actual KPI set — still the shape the shift
 * handover's last-12 h table types its rows with (the daily board's own
 * five-row set left this module with S1b). */
export type PlanKpiRow = {
  id: string;
  label: string;
  actual: number;
  target: number;
  unit: string;
  targetLabel: string;
};

/** The shift just handing over, read from the same shift-cadence series the
 * Superintendent landing's shift table reads — not the 24h rows restated
 * (fixer round, MF9). */
export function last12hVsPlan(state: SessionState): Last12hVsPlan {
  const window = previousShiftWindow(state.cursor);
  const milledSeriesId = window.kind === 'night' ? 'rs-milled-night' : 'rs-milled-day';
  const milled = seriesValueAt(milledSeriesId, state, window.start) ?? 0;
  const pbRec = seriesValueAt('rs-pb-rec-shift', state, window.start) ?? 0;
  const znRec = seriesValueAt('rs-zn-rec-shift', state, window.start) ?? 0;
  const znGrade = seriesValueAt('rs-zn-grade', state, window.start) ?? 0;

  const oreReading = readingByIdLookup('rd-kpi-ore-td');
  const oreTarget12h = (oreReading?.target ?? 0) / 2;
  const pbTarget = readingByIdLookup('rd-kpi-pb-rec')?.target ?? 0;
  const znTarget = readingByIdLookup('rd-kpi-zn-rec')?.target ?? 0;
  const gradeDesign = readingByIdLookup('rd-kpi-zn-grade')?.design ?? 0;

  const raw: readonly PlanKpiRow[] = [
    { id: 'throughput', label: 'Milled tonnes', actual: milled, target: oreTarget12h, unit: 't', targetLabel: 'Budget' },
    { id: 'pb-rec', label: 'Pb recovery', actual: pbRec, target: pbTarget, unit: '%', targetLabel: 'Budget' },
    { id: 'zn-rec', label: 'Zn recovery', actual: znRec, target: znTarget, unit: '%', targetLabel: 'Budget' },
    { id: 'zn-grade', label: 'Zn concentrate grade', actual: znGrade, target: gradeDesign, unit: '%', targetLabel: 'Design' },
  ];

  const rows = raw.map((row) => {
    const deltaPct = row.target === 0 ? 0 : ((row.actual - row.target) / row.target) * 100;
    const tone: 'ok' | 'warn' | 'bad' = deltaPct >= 0 ? 'ok' : deltaPct >= -5 ? 'warn' : 'bad';
    return { ...row, deltaPct, tone };
  });

  return { windowLabel: window.label, rows };
}

/** The crew handing over — the crew on the shift before the cursor's own
 *  (day cursor → the night crew that came off; night cursor → the same day's
 *  day crew), from the [syn] roster, including short-staffing (fixer round,
 *  SF3; #777). `previousCrewForShift` is the shared helper the landing's
 *  carried card reads too, so the two surfaces can never name different
 *  crews. */
export function handoverCrew(state: SessionState): { label: string; headcount: number; seniors: number; shortStaffed: boolean } {
  const crew = previousCrewForShift(state.cursor);
  return {
    label: crewLabelById(crew.crewId),
    headcount: crew.headcount,
    seniors: crew.seniors,
    shortStaffed: crew.headcount < 6,
  };
}

// --- Step 3 · Next 24 hours — the 3W register by area -----------------------

export type AreaGroup = { area: ProcessArea; entries: readonly ThreeWEntry[] };

export function threeWSortRank(row: ThreeWEntry): number {
  if (row.cccCaseId || row.escalated) return 0;
  if (row.source === 'control check' || row.source === 'condition monitoring') return 1;
  if (row.safety) return 2;
  if (row.currentCommit !== null && row.currentCommit !== row.originalCommit) return 3;
  return 4;
}

export type ThreeWPriority = 'critical' | 'high' | 'standard';

export const PRIORITY_LABEL: Readonly<Record<Exclude<ThreeWPriority, 'standard'>, string>> = {
  critical: 'Critical',
  high: 'High',
};

/** The register's priority, read off `threeWSortRank` rather than restated, so
 * the pill and the row order can never disagree: the two ranks the room acts
 * on first (CCC / escalated, control-check / condition-monitoring source) are
 * the two that carry a pill. A safety-only row stays `standard` — the `S`
 * tick already carries safety, and a second signal for it would be a
 * duplicate. */
export function threeWPriority(row: ThreeWEntry): ThreeWPriority {
  const rank = threeWSortRank(row);
  return rank === 0 ? 'critical' : rank === 1 ? 'high' : 'standard';
}

export type ThreeWStatus = 'done-awaiting-close' | 'no-date' | 'slipped' | 'overdue' | 'on-track';

export const STATUS_LABEL: Readonly<Record<ThreeWStatus, string>> = {
  // `Previous 3Ws` says `Done — awaiting close`; in the 134 px Commit column that
  // wraps to three lines on the least urgent rows, so the pill keeps the word.
  'done-awaiting-close': 'Done',
  'no-date': 'No new date',
  slipped: 'Slipped',
  overdue: 'Overdue',
  'on-track': 'On track',
};

/** The pill tone per status — shared by the register and the next-day plan so
 * one status reads the same on both tabs. */
export const STATUS_TONE: Record<ThreeWStatus, 'default' | 'danger' | 'success' | 'warning'> = {
  'done-awaiting-close': 'success',
  'no-date': 'danger',
  // 16 of the 24 seed rows are slipped: amber on all of them would be
  // wallpaper and would borrow the priority pill's colour, so the common
  // state reads neutral and the rare ones carry the tone.
  slipped: 'default',
  overdue: 'danger',
  'on-track': 'default',
};

/** Where one register row stands against its committed date at `asOf` — the
 * meeting day by default, the same idiom `previousThreeWs` uses. `slipped`
 * carries the exact predicate `previousThreeWs` buckets on (a re-commit past
 * `originalCommit`), so the two readings of one row agree. Derived, with no
 * control: the room's own write path is out of this pass (module map § 7.6). */
export function threeWStatus(row: ThreeWEntry, asOf: string = MEETING_CURSOR): ThreeWStatus {
  if (row.doneAwaitingAck) return 'done-awaiting-close';
  if (row.currentCommit === null) return 'no-date';
  if (row.currentCommit > row.originalCommit) return 'slipped';
  if (row.currentCommit < asOf) return 'overdue';
  return 'on-track';
}

/** Bottleneck-area-first (v19 §2, §12); within an area, CCC/escalated rows
 * first, then control-check / condition-monitoring sources, then safety,
 * then slipped (fixer round, MF1). */
export function registerByArea(): readonly AreaGroup[] {
  return AREA_ORDER_BOTTLENECK_FIRST.map((area) => ({
    area,
    entries: THREE_W_ENTRIES.filter((row) => row.area === area)
      .slice()
      .sort((a, b) => threeWSortRank(a) - threeWSortRank(b) || a.id.localeCompare(b.id)),
  }));
}

export type ParetoRow = { area: ProcessArea; count: number };

/** 30-day Pareto of 3W rows by area — open + closed rows raised in the
 * window (`PARETO_30D_COUNTS`), distinct from today's 24 open rows (fixer
 * round, MF5). */
export function paretoByArea(): readonly ParetoRow[] {
  return PROCESS_AREAS.map((area) => ({ area, count: PARETO_30D_COUNTS[area] })).sort(
    (a, b) => b.count - a.count,
  );
}

// --- Header strip KPIs -------------------------------------------------

export type MeetingKpis = {
  onTimeStartPct: number;
  withinWindowPct: number;
  controlVerificationRatePct: number;
  openThreeWCount: number;
  agedOver14dCount: number;
  cccRequiredTodayCount: number;
};

function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

/** Open = not yet acknowledged closed by the room — the same basis Shift
 * handover's carried list uses (fixer round, MF8: one count for one noun). */
export function openThreeWs(): readonly ThreeWEntry[] {
  return THREE_W_ENTRIES.filter((row) => !row.doneAwaitingAck);
}

export function meetingKpis(days = 30, cursor: string = MEETING_CURSOR): MeetingKpis {
  const cutoff = daysBetween('2026-01-01', cursor) - days;
  const window = ALL_MEETING_INSTANCES.filter(
    (m) => daysBetween('2026-01-01', m.date) > cutoff,
  );
  const onTimeStartPct = Math.round(
    (window.filter((m) => m.onTimeStart).length / window.length) * 100,
  );
  const withinWindowPct = Math.round(
    (window.filter((m) => m.finishedWithinWindow).length / window.length) * 100,
  );
  const controlVerificationRatePct = Math.round(
    (CONTROL_VERIFICATION_CHECKS.filter((c) => c.result === 'pass').length /
      CONTROL_VERIFICATION_CHECKS.length) *
      100,
  );
  const open = openThreeWs();
  const agedOver14dCount = open.filter((row) => daysBetween(row.originalCommit, cursor) > 14).length;
  const cccRequiredTodayCount = THREE_W_ENTRIES.filter(
    (row) => row.originalCommit === cursor && Boolean(row.cccCaseId),
  ).length;
  return {
    onTimeStartPct,
    withinWindowPct,
    controlVerificationRatePct,
    openThreeWCount: open.length,
    agedOver14dCount,
    cccRequiredTodayCount,
  };
}

// --- Shift handover -----------------------------------------------------

export type HandoverView = {
  safetyItems: readonly HandoverSafetyItem[];
  plan: Last12hVsPlan;
  carried: readonly ThreeWEntry[];
  tasks: readonly HandoverTask[];
  checks: readonly ControlVerificationCheck[];
};

/** The handover is a filtered view of the same register, not a second
 * list — every row here also appears in Step 3's register. Filtered to
 * rows that touch this shift: CCC/escalated, control-check or
 * condition-monitoring sources, safety-flagged, or committed within the
 * next production day (fixer round, MF10). */
export function isoAddDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function handoverForShift(state: SessionState): HandoverView {
  const nextProductionDay = isoAddDays(MEETING_CURSOR, 1);
  const touchesThisShift = (row: ThreeWEntry) =>
    Boolean(row.cccCaseId) ||
    row.escalated ||
    row.source === 'control check' ||
    row.source === 'condition monitoring' ||
    row.safety ||
    (row.currentCommit !== null && row.currentCommit <= nextProductionDay);

  const carried = THREE_W_ENTRIES.filter((row) => !row.doneAwaitingAck && touchesThisShift(row))
    .slice()
    .sort((a, b) => threeWSortRank(a) - threeWSortRank(b) || a.id.localeCompare(b.id));

  return {
    safetyItems: HANDOVER_SAFETY_ITEMS,
    plan: last12hVsPlan(state),
    carried,
    tasks: HANDOVER_TASKS,
    checks: CONTROL_VERIFICATION_CHECKS,
  };
}

// --- CCC review -----------------------------------------------------------

const STATUS_RANK: Record<CCCCase['status'], number> = {
  Overdue: 0,
  'Recurred — reopened': 1,
  Open: 2,
  'Closed — held': 3,
};

export type CccReviewView = {
  periodLabel: string;
  cases: readonly CCCCase[];
  headline: {
    open: number;
    overdue: number;
    recurredInWindow: number;
    heldTenCycles: number;
  };
};

/** Overdue against the committed review date first — Tier 3's own
 * question, distinct from any single case's progress. */
export function cccReview(cursor: string = MEETING_CURSOR): CccReviewView {
  const cases = [...CCC_CASES].sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status]);
  return {
    periodLabel: formatMonthYear(cursor),
    cases,
    headline: {
      open: CCC_CASES.filter((c) => c.status !== 'Closed — held').length,
      overdue: CCC_CASES.filter((c) => c.status === 'Overdue').length,
      recurredInWindow: CCC_CASES.filter((c) => c.recurred).length,
      heldTenCycles: CCC_CASES.filter((c) => c.cycleCount >= 10).length,
    },
  };
}

/** Root-cause reviewer displayed beside the seat it's routed to — the
 * senior metallurgist seat is filled (L. Chen); the senior reliability
 * engineer seat has no assigned person, so the seat label stands alone
 * (fixer round, MF13). */
export function rootCauseReviewerLabel(routedTo: CCCCase['routedTo']): string {
  return routedTo === 'Senior metallurgist' ? 'L. Chen · Senior metallurgist' : 'Senior reliability engineer';
}
